import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { distinctUntilChanged, filter, map, take } from 'rxjs/operators';

import { ApKeepAliveTimerService } from './keep-alive-timer.service';

/**
 * @ngdoc service
 * @name ApKeepAliveService
 * @description
 */
@Injectable({
    providedIn: 'root'
})
export class ApKeepAliveService {
    private readonly timer = inject(ApKeepAliveTimerService);

    public timeLeft$: Observable<number>;
    public heartbeatTimePassed$: Observable<number>;
    public thresholdIsReached$: Observable<boolean>;
    public timeToEndIsReached$: Observable<boolean>;

    /**
     * @ngdoc method
     * @name ApKeepAliveService#startTimer
     * @methodOf ApKeepAliveService
     *
     * @param timeToThreshold: amount of milliseconds to pass before the threshold is reached.
     * @param timeToEnd: amount of milliseconds to pass before the timer runs out.
     *
     * @description
     * Starts the keep alive timer.
     * Fires thresholdIsReaches$ if the counter reaches timeToThreshold.
     * Fires timeToEndIsReaches$ if the counter reaches timeToEnd.
     */
    public startTimer(timeToThreshold: number, timeToEnd: number): void {
        if (this.timer.isRunning()) {
            this.timer.reset();
        } else {
            this.timer.start();
        }
        this.timeLeft$ = this.timer.timePassed$.pipe(map((timePassed) => timeToEnd - timePassed));

        this.heartbeatTimePassed$ = this.timer.heartbeatTimePassed$;

        this.thresholdIsReached$ = this.timer.timePassed$.pipe(
            map((timePassed) => timePassed >= timeToThreshold),
            distinctUntilChanged()
        );

        this.timeToEndIsReached$ = this.timer.timePassed$.pipe(
            map((timePassed) => timePassed >= timeToEnd),
            filter((isReached) => isReached),
            take(1)
        );
    }

    /**
     * @ngdoc method
     * @name ApKeepAliveService#resetTimer
     * @methodOf ApKeepAliveService
     *
     * @description
     * Calls the timer's reset method to reset the keep alive timer.
     */
    public resetTimer(): void {
        this.timer.reset();
    }

    /**
     * @ngdoc method
     * @name ApKeepAliveService#resetHeartbeatTimer
     * @methodOf ApKeepAliveService
     *
     * @description
     * Calls the timer's resetHeartbeat method to reset the heart beat timer.
     */
    public resetHeartbeatTimer(): void {
        this.timer.resetHeartbeat();
    }
}
